/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Animate directive options container
 *
 * **Available Options:**
 *  - `targets`:
 *    - CSS selector string to find target elements within the directive element.
 *    - If empty or 'self', the directive element itself will be animated.
 *    - Can be a selector like '[class*="col-"]' to target multiple elements.
 *
 *  - `class`:
 *    - The class name to add when element enters viewport.
 *    - Defaults to 'in-view'.
 *
 *  - `threshold`:
 *    - Visibility threshold (0-1) for triggering animation.
 *    - Defaults to 0.1 (10% visible).
 *
 *  - `once`:
 *    - If true, disconnect observer after first trigger.
 *    - If false, class will be toggled on enter/leave.
 *    - Defaults to true.
 *
 *  - `stagger`:
 *    - Delay between each target element animation in milliseconds.
 *    - Set to 0 for no stagger. Defaults to 0.
 *
 *  - `rootMargin`:
 *    - Margin around the root viewport.
 *    - Can be used to trigger animation before element is fully in view.
 *    - Defaults to '0px'.
 *
 * @example
 *
 * ```html
 * <!-- Basic usage - animate the element itself -->
 * <div amb-animate>Content</div>
 *
 * <!-- With preset -->
 * <div amb-animate="carousel">Content</div>
 *
 * <!-- With custom options -->
 * <div amb-animate data-targets=".item" data-threshold="0.2" data-stagger="100">
 *   <div class="item">Item 1</div>
 *   <div class="item">Item 2</div>
 * </div>
 *
 * <!-- Inline JSON options -->
 * <div amb-animate='{"targets": ".card", "class": "visible", "once": true}'>
 *   <div class="card">Card 1</div>
 *   <div class="card">Card 2</div>
 * </div>
 * ```
 */
export const animateOptions = {
  /**
   * Default options - animate the element itself
   */
  default: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.1,
    once: true,
    stagger: 0,
    rootMargin: '0px'
  },

  /**
   * Carousel preset - for .common--carousel containers
   * Finds the closest .common--carousel and animates it
   *
   * @example
   *
   * ```html
   * <div class="common--carousel" amb-animate="carousel">
   *   <div amb-swiper="car_features">...</div>
   * </div>
   * ```
   */
  carousel: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.1,
    once: true
  },

  /**
   * Narrative preset - for column items with staggered animation
   * Targets all elements with class containing "col-"
   *
   * @example
   *
   * ```html
   * <div class="container--narrative" amb-animate="narrative">
   *   <div class="col-md-6">Content 1</div>
   *   <div class="col-md-6">Content 2</div>
   * </div>
   * ```
   */
  narrative: {
    targets: '[class*="col-"]',
    class: 'in-view',
    threshold: 0.1,
    stagger: 150,
    once: true,
    rootMargin: '0px 0px -50px 0px'
  },

  /**
   * Children preset - animate all direct children
   *
   * @example
   *
   * ```html
   * <div amb-animate="children">
   *   <div>Child 1</div>
   *   <div>Child 2</div>
   * </div>
   * ```
   */
  children: {
    targets: ':scope > *',
    class: 'in-view',
    threshold: 0.1,
    once: true,
    stagger: 100
  },

  /**
   * Container preset - generic container animation
   * Useful for sections, cards, or any block element
   *
   * @example
   *
   * ```html
   * <section class="hero" amb-animate="container">
   *   <h1>Hero Title</h1>
   * </section>
   * ```
   */
  container: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.2,
    once: true
  },

  /**
   * Slide-up preset - for elements that slide up on enter
   * Higher threshold to ensure more of element is visible
   *
   * @example
   *
   * ```html
   * <div amb-animate="slide_up">
   *   <p>This content will slide up</p>
   * </div>
   * ```
   */
  slide_up: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.15,
    once: true,
    rootMargin: '0px 0px -50px 0px'
  },

  /**
   * Fade preset - for subtle fade-in animations
   * Lower threshold for earlier trigger
   *
   * @example
   *
   * ```html
   * <div amb-animate="fade">
   *   <img src="..." alt="..." />
   * </div>
   * ```
   */
  fade: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.05,
    once: true
  },

  /**
   * Items preset - for list of items with stagger
   *
   * @example
   *
   * ```html
   * <ul amb-animate="items" data-targets="li">
   *   <li>Item 1</li>
   *   <li>Item 2</li>
   *   <li>Item 3</li>
   * </ul>
   * ```
   */
  items: {
    targets: ':scope > *',
    class: 'in-view',
    threshold: 0.1,
    once: true,
    stagger: 50
  },

  /**
   * Car Features preset - for animating car feature cards
   *
   * @example
   * ```html
   * <div amb-animate="car_features">
   *   <div>A</div>
   *   <div>B</div>
   * </div>
   * ```
   */
  car_features: {
    targets: 'self',
    class: 'in-view',
    threshold: 0.1,
    once: true,
    stagger: 80,
    rootMargin: '0px 0px -32px 0px'
  },

  grade_carousel: {
    targets: ".common--vehicle-grades-carousel",
    class: "in-view",
    threshold: 0.1,
    once: true,
    stagger: 80,
    rootMargin: "0px 0px -32px 0px"
  },

  offers_carousel: {
    targets: 'self',
    class: "in-view",
    threshold: 0.1,
    once: true,
    stagger: 80,
    rootMargin: "0px 0px -32px 0px"
  }
};
